<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';

import Button from 'dashboard/components-next/button/Button.vue';

const store = useStore();
const router = useRouter();
const { t } = useI18n();

const searchQuery = ref('');

// Inline editor panel state (create + edit reuse the same form).
const isFormOpen = ref(false);
const editingId = ref(null);
const form = ref({ name: '', category: '', description: '' });

const playbooks = computed(() => store.getters.getPlaybooks);
const uiFlags = computed(() => store.getters.getPlaybookUIFlags);

const filteredPlaybooks = computed(() => {
  const q = searchQuery.value.trim().toLowerCase();
  if (!q) return playbooks.value;
  return playbooks.value.filter(p =>
    [p.name, p.category, p.description].some(v =>
      (v || '').toLowerCase().includes(q)
    )
  );
});

const isEmpty = computed(
  () => !uiFlags.value.isFetching && !filteredPlaybooks.value.length
);

const isSaving = computed(
  () => uiFlags.value.isCreating || uiFlags.value.isUpdating
);

const loadPlaybooks = () => store.dispatch('fetchPlaybooks');

const resetForm = () => {
  editingId.value = null;
  form.value = { name: '', category: '', description: '' };
};

const openCreate = () => {
  resetForm();
  isFormOpen.value = true;
};

const openEdit = playbook => {
  editingId.value = playbook.id;
  form.value = {
    name: playbook.name || '',
    category: playbook.category || '',
    description: playbook.description || '',
  };
  isFormOpen.value = true;
};

const closeForm = () => {
  isFormOpen.value = false;
  resetForm();
};

const submitForm = async () => {
  const name = form.value.name.trim();
  if (!name || isSaving.value) return;
  const payload = {
    name,
    category: form.value.category.trim() || null,
    description: form.value.description.trim() || null,
  };
  try {
    if (editingId.value) {
      await store.dispatch('updatePlaybook', {
        id: editingId.value,
        ...payload,
      });
      useAlert(t('PROJECTS.PLAYBOOKS.API.UPDATE_SUCCESS'));
    } else {
      await store.dispatch('createPlaybook', payload);
      useAlert(t('PROJECTS.PLAYBOOKS.API.CREATE_SUCCESS'));
    }
    closeForm();
  } catch (error) {
    useAlert(t('PROJECTS.PLAYBOOKS.API.ERROR'));
  }
};

const deletePlaybook = async playbook => {
  // eslint-disable-next-line no-alert
  if (!window.confirm(t('PROJECTS.PLAYBOOKS.DELETE_CONFIRM'))) return;
  try {
    await store.dispatch('deletePlaybook', playbook.id);
    useAlert(t('PROJECTS.PLAYBOOKS.API.DELETE_SUCCESS'));
  } catch (error) {
    useAlert(t('PROJECTS.PLAYBOOKS.API.ERROR'));
  }
};

const openEditor = playbook =>
  router.push({
    name: 'playbook_editor',
    params: { playbookId: playbook.id },
  });

const goBack = () => router.push({ name: 'projects_list' });

onMounted(loadPlaybooks);
</script>

<template>
  <div class="flex flex-col w-full h-full bg-n-background">
    <!-- Toolbar -->
    <header
      class="flex items-center justify-between gap-3 px-6 pt-5 pb-3 border-b shrink-0 border-n-weak"
    >
      <div class="flex items-center gap-2 min-w-0">
        <button
          type="button"
          class="flex items-center justify-center rounded-lg size-8 text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-1 shrink-0"
          :title="t('PROJECTS.ACTIONS.BACK')"
          @click="goBack"
        >
          <span class="i-lucide-arrow-left size-4" />
        </button>
        <div class="flex flex-col min-w-0">
          <h1
            class="flex items-center gap-2 text-xl font-semibold text-n-slate-12"
          >
            {{ t('PROJECTS.PLAYBOOKS.LIST') }}
            <span class="text-base font-normal text-n-slate-10">
              {{ playbooks.length }}
            </span>
          </h1>
          <p class="text-sm text-n-slate-10">
            {{ t('PROJECTS.PLAYBOOKS.LIST_SUBTITLE') }}
          </p>
        </div>
      </div>
      <div class="flex items-center gap-2">
        <div class="relative w-52 max-w-full">
          <span
            class="absolute -translate-y-1/2 i-lucide-search left-2.5 top-1/2 text-n-slate-10 size-4"
          />
          <input
            v-model="searchQuery"
            type="text"
            :placeholder="t('PROJECTS.PLAYBOOKS.SEARCH_PLACEHOLDER')"
            class="w-full h-8 pl-8 pr-3 text-sm border-none rounded-lg reset-base outline-1 outline outline-n-weak bg-n-alpha-black2 text-n-slate-12 placeholder:text-n-slate-10 focus:outline-n-brand"
          />
        </div>
        <Button
          :label="t('PROJECTS.PLAYBOOKS.NEW')"
          icon="i-lucide-plus"
          size="sm"
          color="blue"
          @click="openCreate"
        />
      </div>
    </header>

    <!-- Inline create / edit form -->
    <div
      v-if="isFormOpen"
      class="flex flex-col gap-3 px-6 py-4 border-b border-n-weak bg-n-alpha-1/40 shrink-0"
    >
      <div class="flex flex-col gap-3 sm:flex-row">
        <label class="flex flex-col flex-1 gap-1">
          <span class="text-xs font-medium text-n-slate-11">
            {{ t('PROJECTS.PLAYBOOKS.FIELDS.NAME') }}
          </span>
          <input
            v-model="form.name"
            type="text"
            :placeholder="t('PROJECTS.PLAYBOOKS.NAME_PLACEHOLDER')"
            class="h-9 px-3 text-sm border rounded-lg reset-base bg-n-solid-1 border-n-weak text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:outline-1 focus:outline-n-brand"
            @keyup.enter="submitForm"
            @keyup.esc="closeForm"
          />
        </label>
        <label class="flex flex-col flex-1 gap-1">
          <span class="text-xs font-medium text-n-slate-11">
            {{ t('PROJECTS.PLAYBOOKS.FIELDS.CATEGORY') }}
          </span>
          <input
            v-model="form.category"
            type="text"
            :placeholder="t('PROJECTS.PLAYBOOKS.CATEGORY_PLACEHOLDER')"
            class="h-9 px-3 text-sm border rounded-lg reset-base bg-n-solid-1 border-n-weak text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:outline-1 focus:outline-n-brand"
            @keyup.enter="submitForm"
            @keyup.esc="closeForm"
          />
        </label>
      </div>
      <label class="flex flex-col gap-1">
        <span class="text-xs font-medium text-n-slate-11">
          {{ t('PROJECTS.PLAYBOOKS.FIELDS.DESCRIPTION') }}
        </span>
        <textarea
          v-model="form.description"
          rows="2"
          :placeholder="t('PROJECTS.PLAYBOOKS.DESCRIPTION_PLACEHOLDER')"
          class="px-3 py-2 text-sm border rounded-lg resize-y reset-base bg-n-solid-1 border-n-weak text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:outline-1 focus:outline-n-brand"
        />
      </label>
      <div class="flex items-center justify-end gap-2">
        <Button
          variant="ghost"
          color="slate"
          size="sm"
          :label="t('PROJECTS.PLAYBOOKS.ACTIONS.CANCEL')"
          @click="closeForm"
        />
        <Button
          color="blue"
          size="sm"
          :label="
            editingId
              ? t('PROJECTS.PLAYBOOKS.ACTIONS.SAVE')
              : t('PROJECTS.PLAYBOOKS.ACTIONS.CREATE')
          "
          :is-loading="isSaving"
          :disabled="!form.name.trim()"
          @click="submitForm"
        />
      </div>
    </div>

    <!-- Body -->
    <section class="flex-1 min-h-0 overflow-y-auto">
      <!-- Loading skeleton -->
      <div
        v-if="uiFlags.isFetching && !playbooks.length"
        class="divide-y divide-n-weak/60"
      >
        <div
          v-for="n in 5"
          :key="n"
          class="flex items-center gap-3 px-6 py-4 animate-pulse"
        >
          <div class="flex flex-col gap-1.5">
            <span class="h-3 rounded w-40 bg-n-alpha-2" />
            <span class="h-2.5 rounded w-24 bg-n-alpha-1" />
          </div>
        </div>
      </div>

      <!-- Empty state -->
      <div
        v-else-if="isEmpty"
        class="flex flex-col items-center justify-center h-full gap-4 px-6 text-center"
      >
        <span
          class="flex items-center justify-center rounded-2xl size-14 bg-n-alpha-1 text-n-slate-10"
        >
          <span class="i-lucide-book-open size-7" />
        </span>
        <div class="flex flex-col gap-1">
          <p class="text-base font-medium text-n-slate-12">
            {{
              searchQuery
                ? t('PROJECTS.PLAYBOOKS.NO_RESULTS')
                : t('PROJECTS.PLAYBOOKS.EMPTY_TITLE')
            }}
          </p>
          <p class="max-w-sm text-sm text-n-slate-10">
            {{ t('PROJECTS.PLAYBOOKS.EMPTY_HINT') }}
          </p>
        </div>
        <Button
          v-if="!searchQuery && !isFormOpen"
          :label="t('PROJECTS.PLAYBOOKS.NEW')"
          icon="i-lucide-plus"
          size="sm"
          color="blue"
          @click="openCreate"
        />
      </div>

      <!-- List -->
      <div v-else class="flex flex-col">
        <!-- Column header -->
        <div
          class="flex items-center gap-3 px-6 py-2 text-xs border-b border-n-weak text-n-slate-10 shrink-0"
        >
          <span class="flex-1 min-w-0">
            {{ t('PROJECTS.PLAYBOOKS.COL.NAME') }}
          </span>
          <span class="hidden sm:block w-40 shrink-0">
            {{ t('PROJECTS.PLAYBOOKS.COL.CATEGORY') }}
          </span>
          <span class="w-20 text-right shrink-0">
            {{ t('PROJECTS.PLAYBOOKS.COL.PHASES') }}
          </span>
          <span class="w-24 shrink-0" />
        </div>

        <div class="divide-y divide-n-weak/60">
          <div
            v-for="playbook in filteredPlaybooks"
            :key="playbook.id"
            class="flex items-center gap-3 px-6 py-3 cursor-pointer group hover:bg-n-alpha-1"
            @click="openEditor(playbook)"
          >
            <div class="flex flex-col flex-1 min-w-0">
              <span class="text-sm font-medium truncate text-n-slate-12">
                {{ playbook.name }}
              </span>
              <span
                v-if="playbook.description"
                class="text-xs truncate text-n-slate-10"
              >
                {{ playbook.description }}
              </span>
            </div>
            <span
              class="hidden sm:block w-40 text-sm truncate shrink-0"
              :class="
                playbook.category ? 'text-n-slate-11' : 'text-n-slate-9 italic'
              "
            >
              {{ playbook.category || t('PROJECTS.PLAYBOOKS.NO_CATEGORY') }}
            </span>
            <span
              class="w-20 text-sm text-right tabular-nums shrink-0 text-n-slate-11"
            >
              {{ playbook.phases_count ?? 0 }}
            </span>
            <div
              class="flex items-center justify-end w-24 gap-0.5 shrink-0"
              @click.stop
            >
              <Button
                v-tooltip.top="t('PROJECTS.PLAYBOOKS.ACTIONS.EDIT')"
                variant="ghost"
                color="slate"
                size="xs"
                icon="i-lucide-pencil"
                class="opacity-0 transition-opacity group-hover:opacity-100"
                @click="openEdit(playbook)"
              />
              <Button
                v-tooltip.top="t('PROJECTS.PLAYBOOKS.ACTIONS.DELETE')"
                variant="ghost"
                color="ruby"
                size="xs"
                icon="i-lucide-trash-2"
                class="opacity-0 transition-opacity group-hover:opacity-100"
                @click="deletePlaybook(playbook)"
              />
              <Button
                v-tooltip.top="t('PROJECTS.PLAYBOOKS.EDIT')"
                variant="ghost"
                color="slate"
                size="xs"
                icon="i-lucide-chevron-right"
                @click="openEditor(playbook)"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>
